import { useEffect, useState } from 'react';
import { WarningCircle, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Textarea } from '@/components/ui-kit/textarea';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useCreateList, useGetLists, useUpdateList } from '../hooks/use-lists';
import type { CampaignList } from '../types/lists.types';
import {
  DUPLICATE_LIST_NAME_MESSAGE,
  LIST_NAME_MAX_LENGTH,
  isDuplicateListNameError,
} from '../utils/list-errors';

export type ListFormModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingList?: CampaignList;
};

const NAME_HELP_DEFAULT = 'Must be unique — this is how contacts and other marketers will find it.';
const SAVE_ERROR_MESSAGE = 'Something went wrong saving this list. Your changes have not been lost — try Save again.';

const fieldClassName =
  'h-10 rounded-md border-neutral-200 bg-white px-3 text-sm text-neutral-900 shadow-none placeholder:text-neutral-400 focus-visible:border-primary-600 focus-visible:ring-2 focus-visible:ring-primary-600/30';

export const ListFormModal = ({ open, onOpenChange, editingList }: ListFormModalProps) => {
  const { toast } = useToast();
  const isEditMode = Boolean(editingList);
  const { mutateAsync: createList, isPending: isCreating } = useCreateList();
  const { mutateAsync: updateList, isPending: isUpdating } = useUpdateList();
  const isSaving = isCreating || isUpdating;
  const { data: listsData } = useGetLists({ pageNo: 1, pageSize: 200 }, { enabled: open });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nameError, setNameError] = useState('');
  const [serverError, setServerError] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(editingList?.name ?? '');
    setDescription(editingList?.description ?? '');
    setNameError('');
    setServerError(false);
  }, [open, editingList]);

  const handleSave = async () => {
    const trimmedName = name.trim();
    const trimmedDescription = description.trim();

    if (!trimmedName) {
      setNameError('Name is required.');
      return;
    }

    if (trimmedName.length > LIST_NAME_MAX_LENGTH) {
      setNameError(`Name must be ${LIST_NAME_MAX_LENGTH} characters or fewer.`);
      return;
    }

    const isDuplicate = (listsData?.items ?? []).some(
      (existing) =>
        existing.id !== editingList?.id &&
        existing.name.trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (isDuplicate) {
      setNameError(DUPLICATE_LIST_NAME_MESSAGE);
      return;
    }

    setServerError(false);

    try {
      if (isEditMode && editingList?.id) {
        await updateList({
          listId: editingList.id,
          input: { name: trimmedName, description: trimmedDescription },
        });
        toast({
          variant: 'success',
          title: `List updated.`,
        });
      } else {
        await createList({ name: trimmedName, description: trimmedDescription });
        toast({
          variant: 'success',
          title: `List "${trimmedName}" created.`,
        });
      }
      onOpenChange(false);
    } catch (error) {
      if (isDuplicateListNameError(error)) {
        setNameError(DUPLICATE_LIST_NAME_MESSAGE);
        return;
      }
      setServerError(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        overlayClassName="bg-black/40"
        className="flex max-h-[90vh] w-full max-w-[440px] flex-col gap-0 overflow-hidden rounded-lg border-neutral-200 p-0 shadow-e4 sm:max-w-[440px]"
      >
        <DialogHeader className="flex h-14 flex-none flex-row items-center justify-between space-y-0 border-b border-neutral-200 px-5">
          <DialogTitle className="text-base font-semibold leading-6 text-neutral-900">
            {isEditMode ? 'Edit list' : 'New list'}
          </DialogTitle>
          <DialogClose className="inline-flex size-8 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800">
            <X className="size-[18px]" aria-hidden />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          <div className="mb-4">
            <Label htmlFor="lf-name" className="mb-1.5 block text-[13px] font-medium text-neutral-800">
              Name
            </Label>
            <Input
              id="lf-name"
              autoFocus
              placeholder="e.g. Newsletter subscribers"
              value={name}
              maxLength={LIST_NAME_MAX_LENGTH}
              className={cn(
                fieldClassName,
                Boolean(nameError) && 'border-error focus-visible:border-error focus-visible:ring-error/25'
              )}
              onChange={(event) => {
                setName(event.target.value);
                setNameError('');
              }}
            />
            <p
              id="lf-name-help"
              className={cn(
                'mt-1.5 text-xs',
                nameError ? 'flex items-center gap-1 text-error' : 'text-neutral-600'
              )}
              role={nameError ? 'alert' : undefined}
            >
              {nameError ? <WarningCircle className="size-3.5" aria-hidden /> : null}
              {nameError || NAME_HELP_DEFAULT}
            </p>
          </div>

          <div>
            <Label htmlFor="lf-description" className="mb-1.5 block text-[13px] font-medium text-neutral-800">
              Description
            </Label>
            <Textarea
              id="lf-description"
              rows={3}
              placeholder="What is this list for? (optional)"
              value={description}
              className="min-h-[84px] rounded-md border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 shadow-none placeholder:text-neutral-400 focus-visible:border-primary-600 focus-visible:ring-2 focus-visible:ring-primary-600/30"
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>

          {serverError && (
            <div className="mt-4 flex items-start gap-2.5 rounded-lg bg-error-background px-3 py-2.5">
              <WarningCircle className="mt-0.5 size-4 shrink-0 text-error" aria-hidden />
              <p className="text-xs text-error">{SAVE_ERROR_MESSAGE}</p>
            </div>
          )}
        </div>

        <DialogFooter className="flex-none flex-row items-center justify-end gap-2 border-t border-neutral-200 px-5 py-4 sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            className="h-10 px-4 text-neutral-800 hover:bg-neutral-100"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="h-10 bg-primary-600 px-4 text-white hover:bg-primary-700"
            disabled={isSaving}
            loading={isSaving}
            onClick={() => void handleSave()}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
